import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { AiBriefCard } from "@/components/AiBriefCard";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { TaskDetailDialog, EventDetailDialog } from "@/components/EntityDialogs";
import { colorMeta } from "@/lib/colors";
import {
  Users, CheckSquare, RefreshCw, Bell, ArrowRight, Mic,
  AlertTriangle, Clock, ChevronRight, CheckCircle2,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

export default function Dashboard() {
  const navigate = useNavigate();
  const { openVoice } = useOutletContext();
  const { people, tasks, renewals, alerts, events, notes, taskCategories, resolveAlert } = useFgp();

  const [taskId, setTaskId] = useState(null);
  const [selEvent, setSelEvent] = useState(null);
  const [eventOpen, setEventOpen] = useState(false);
  const detailTask = tasks.find((t) => t.id === taskId) || null;
  const catOf = (id) => taskCategories.find((c) => c.id === id);
  const openEvent = (e) => { setSelEvent(e); setEventOpen(true); };

  const openTasks = tasks.filter((t) => t.status !== "done");
  const overdueRenewals = renewals.filter((r) => r.status === "overdue").length;
  const dueSoon = renewals.filter((r) => r.status === "due-soon").length;

  const stats = [
    { label: "People", value: people.length, icon: Users, tone: "blue", sub: "1 principal · linked network", testid: "stat-people", to: "/app/people" },
    { label: "Open tasks", value: openTasks.length, icon: CheckSquare, tone: "amber", sub: `${tasks.filter(t=>t.status==='overdue').length} overdue`, testid: "stat-tasks", to: "/app/tasks" },
    { label: "Renewals due", value: overdueRenewals + dueSoon, icon: RefreshCw, tone: "rose", sub: `${overdueRenewals} overdue · ${dueSoon} soon`, testid: "stat-renewals", to: "/app/renewals" },
    { label: "Active alerts", value: alerts.length, icon: Bell, tone: "default", sub: "across the household", testid: "stat-alerts", to: "/app/alerts" },
  ];

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="A calm overview of everything happening across the household today."
        actions={<Button onClick={openVoice} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="dashboard-voice"><Mic className="h-4 w-4" /> Voice note</Button>}
      />

      <AiBriefCard />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {stats.map((s) => <StatCard key={s.label} {...s} onClick={() => navigate(s.to)} />)}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        {/* Priority tasks */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-slate-900">Priority tasks</h2>
            <button onClick={() => navigate("/app/tasks")} className="text-sm text-neutral-600 font-medium flex items-center gap-1 hover:gap-1.5 transition-all" data-testid="dash-view-tasks">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="space-y-1">
            {openTasks.slice(0, 5).map((t) => {
              const cat = catOf(t.category);
              const cm = colorMeta(cat?.color);
              return (
                <button key={t.id} onClick={() => setTaskId(t.id)} data-testid={`dash-task-${t.id}`}
                  className="w-full flex items-center gap-3 py-2.5 border-b border-slate-50 last:border-0 text-left hover:bg-slate-50 rounded-lg px-1.5 -mx-1.5 transition-colors">
                  <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${cm.dot}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{t.title}</p>
                    <p className="text-xs text-slate-400">{cat ? cat.name + " · " : ""}{t.person} · {t.assignee}</p>
                  </div>
                  <StatusBadge status={t.status} />
                  <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Critical alerts */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-slate-900">Needs attention</h2>
            <button onClick={() => navigate("/app/alerts")} className="text-sm text-neutral-600 font-medium" data-testid="dash-view-alerts">All</button>
          </div>
          <div className="space-y-3">
            {alerts.length === 0 && (
              <div className="text-center py-8" data-testid="dash-alerts-empty">
                <CheckCircle2 className="h-7 w-7 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">All clear</p>
                <p className="text-xs text-slate-400">Nothing needs your attention right now.</p>
              </div>
            )}
            {alerts.slice(0, 4).map((a) => (
              <div key={a.id} data-testid={`dash-alert-${a.id}`} className="group flex items-start gap-3 rounded-lg p-1.5 -m-1.5 hover:bg-slate-50 transition-colors">
                <span className={`h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0 ${a.severity === "critical" ? "bg-rose-50 text-rose-600" : a.severity === "warning" ? "bg-amber-50 text-amber-600" : "bg-neutral-50 text-neutral-600"}`}>
                  <AlertTriangle className="h-3.5 w-3.5" />
                </span>
                <button onClick={() => navigate("/app/alerts")} className="flex-1 min-w-0 text-left" data-testid={`dash-alert-open-${a.id}`}>
                  <p className="text-sm font-medium text-slate-800 leading-snug">{a.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{a.time}</p>
                </button>
                <button onClick={() => { resolveAlert(a.id); toast.success("Alert resolved"); }} data-testid={`dash-alert-resolve-${a.id}`}
                  title="Resolve" className="flex-shrink-0 h-7 w-7 rounded-lg flex items-center justify-center text-slate-300 hover:text-emerald-600 hover:bg-emerald-50 transition-colors">
                  <CheckCircle2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        {/* Upcoming */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-slate-900">Upcoming this week</h2>
            <button onClick={() => navigate("/app/calendar")} className="text-sm text-neutral-600 font-medium flex items-center gap-1" data-testid="dash-view-calendar">Calendar <ArrowRight className="h-3.5 w-3.5" /></button>
          </div>
          <div className="space-y-1">
            {events.slice(0, 5).map((e) => (
              <button key={e.id} onClick={() => openEvent(e)} data-testid={`dash-event-${e.id}`}
                className="w-full flex items-center gap-4 py-2.5 border-b border-slate-50 last:border-0 text-left hover:bg-slate-50 rounded-lg px-1.5 -mx-1.5 transition-colors">
                <div className="text-center w-12 flex-shrink-0">
                  <p className="text-xs text-slate-400">{new Date(e.date).toLocaleDateString("en-GB", { month: "short" })}</p>
                  <p className="font-heading text-lg font-semibold text-slate-800 leading-none">{new Date(e.date).getDate()}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{e.title}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> {e.time} · {e.location}</p>
                </div>
                <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full hidden sm:inline">{e.category}</span>
                <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
              </button>
            ))}
          </div>
        </motion.div>

        {/* Recent voice notes */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-white to-neutral-50/40 rounded-xl border border-neutral-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Mic className="h-4 w-4 text-neutral-600" />
            <h2 className="font-heading text-lg font-semibold text-slate-900">Recent notes</h2>
            <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-neutral-600 bg-neutral-100 px-2 py-0.5 rounded-full">AI</span>
          </div>
          <div className="space-y-3">
            {notes.slice(0, 3).map((n) => (
              <button key={n.id} onClick={() => navigate("/app/notes")} className="w-full text-left rounded-lg bg-white border border-slate-200 p-3 hover:border-slate-300 hover:shadow-sm transition-all" data-testid={`dash-note-${n.id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-800">{n.title}</p>
                  <span className="text-xs text-slate-400">{n.duration}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{n.transcript}</p>
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={openVoice} className="w-full mt-4 gap-2" data-testid="dash-new-note"><Mic className="h-4 w-4" /> New voice note</Button>
        </motion.div>
      </div>

      <TaskDetailDialog open={taskId !== null} onOpenChange={(o) => { if (!o) setTaskId(null); }} task={detailTask} />
      <EventDetailDialog open={eventOpen} onOpenChange={setEventOpen} event={selEvent} />
    </div>
  );
}
